import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  MoreVertical,
  Eye,
  Download,
  CheckCircle,
  XCircle,
  Calendar,
  CreditCard,
  Banknote,
  Search,
  Filter,
  User,
  Store,
} from "lucide-react";
import { PaymentRecord, Owner } from "../../lib/adminApi";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useState } from "react";

interface PaymentsTableProps {
  payments: PaymentRecord[];
  loading: boolean;
  onViewDetails: (payment: PaymentRecord) => void;
  onSearch: (query: string) => void;
  onFilterChange: (filter: string) => void;
  currentFilter: string;
  owners: Owner[]; // Cambiamos a Owner[] directamente
}

export function PaymentsTable({
  payments,
  loading,
  onViewDetails,
  onSearch,
  onFilterChange,
  currentFilter,
  owners,
}: PaymentsTableProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch(value);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: es });
    } catch {
      return "Fecha inválida";
    }
  };

  const formatCurrency = (amount: string) => {
    return `${parseFloat(amount).toLocaleString("es-CU")} CUP`;
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case "transfer":
        return <Banknote className="h-4 w-4 mr-2" />;
      case "cash":
        return <CreditCard className="h-4 w-4 mr-2" />;
      default:
        return <CreditCard className="h-4 w-4 mr-2" />;
    }
  };

  const getPaymentMethodText = (method: string) => {
    switch (method.toLowerCase()) {
      case "transfer":
        return "Transferencia";
      case "cash":
        return "Efectivo";
      default:
        return method;
    }
  };

  const getOwnerName = (payment: PaymentRecord) => {
    // Si el pago tiene ownerBusinessName (de la nueva estructura), usarlo
    if ((payment as any).ownerBusinessName) {
      return (payment as any).ownerBusinessName;
    }
    // Fallback: buscar en el array de owners por ownerId
    if ((payment as any).ownerId) {
      const owner = owners.find((o) => o.id === (payment as any).ownerId);
      return owner ? owner.businessName : "Negocio no encontrado";
    }
    // Último fallback: buscar por shopId (para pagos antiguos)
    return "Negocio no encontrado";
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  // NO hacemos return temprano si no hay pagos, solo mostramos mensaje
  const showEmptyState = payments.length === 0;

  return (
    <div className="space-y-4">
      {/* Filtros y búsqueda - SIEMPRE visibles */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar por negocio, referencia o notas..."
            value={searchQuery}
            onChange={handleSearch}
            className="pl-10"
          />
        </div>

        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Filter className="h-4 w-4 mr-2" />
                {currentFilter === "all"
                  ? "Todos"
                  : currentFilter === "verified"
                    ? "Verificados"
                    : currentFilter === "pending"
                      ? "Pendientes"
                      : "Todos"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => onFilterChange("all")}>
                Todos los pagos
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onFilterChange("verified")}>
                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                Verificados
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onFilterChange("pending")}>
                <XCircle className="h-4 w-4 mr-2 text-yellow-600" />
                Pendientes
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Estado vacío o tabla */}
      {showEmptyState ? (
        <div className="text-center py-12">
          <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No se encontraron pagos
          </h3>
          <p className="text-gray-600 mb-4">
            {currentFilter !== "all"
              ? `No hay pagos con el filtro "${currentFilter}"`
              : "No hay pagos registrados en el sistema"}
          </p>
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                onSearch("");
                setSearchQuery("");
              }}
            >
              Limpiar búsqueda
            </Button>
            <Button variant="outline" onClick={() => onFilterChange("all")}>
              Ver todos
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Tabla */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Negocio</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Monto</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Método</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center">
                        <Store className="h-3 w-3 text-gray-400 mr-2" />
                        <div>
                          <div className="font-medium">
                            {getOwnerName(payment)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Ref: {payment.referenceNumber || "N/A"}
                          </div>
                          {(payment as any).ownerEmail && (
                            <div className="text-xs text-gray-500">
                              {(payment as any).ownerEmail}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 text-gray-400 mr-2" />
                        <div className="text-sm">
                          {formatDate(payment.paymentDate)}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="font-semibold text-green-600">
                        {formatCurrency(payment.amount)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {payment.currency}
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="text-sm">
                        {payment.periodCovered === "1_month"
                          ? "1 mes"
                          : payment.periodCovered === "3_months"
                            ? "3 meses"
                            : payment.periodCovered === "6_months"
                              ? "6 meses"
                              : payment.periodCovered === "1_year"
                                ? "1 año"
                                : payment.periodCovered}
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center">
                        {getPaymentMethodIcon(payment.paymentMethod)}
                        <span className="text-sm">
                          {getPaymentMethodText(payment.paymentMethod)}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell>
                      {payment.verifiedByAdmin ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verificado
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-200">
                          <XCircle className="h-3 w-3 mr-1" />
                          Pendiente
                        </Badge>
                      )}
                    </TableCell>

                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => onViewDetails(payment)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Ver detalles
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Download className="h-4 w-4 mr-2" />
                            Descargar recibo
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Resumen - solo cuando hay resultados */}
          <div className="text-sm text-gray-600">
            Mostrando {payments.length} pago{payments.length !== 1 ? "s" : ""}
            {currentFilter !== "all" && ` con estado "${currentFilter}"`}
            <span className="ml-4 font-medium">
              Total:{" "}
              {formatCurrency(
                payments
                  .reduce((sum, payment) => sum + parseFloat(payment.amount), 0)
                  .toString(),
              )}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
